import DeleteIcon from '@mui/icons-material/Delete';
import { Typography, useTheme, Box, Tooltip } from '@mui/material';
import {
  type GridEventListener,
  type GridColumns,
  type GridValidRowModel,
  type GridRowParams,
  type MuiEvent,
  type GridSortModel,
} from '@mui/x-data-grid';

import CustomDataGrid from 'components/customDataGrid/CustomDataGrid';
import { strings } from 'i18n/Strings';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllOganizationData,
  getOrganizationDetailsOrgId,
  removeOganization,
  updateOrganizationIsActive,
  getTotalEmp,
} from 'service/EmployerService';
import { type OganizationAttributesType } from 'types/services';
import { colorTheme } from '../../theme';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import AlertModal from './AlertModal';
import AddEmployer from './AddEmployer';
import { DialogBox } from 'components';
import EditEmployer from './EditEmployer';
import EditIcon from '@mui/icons-material/Edit';
import Switch from '@mui/material/Switch';
import { fetchExportedEmployeeData } from 'service/UserService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import {
  addEmployerConstantTexts,
  exportExcelFileNames,
} from 'constants/constantText';
import ConfirmationModal from 'components/confirmation-modal';

type GridRowData = Record<string, unknown>;

const Employers = (): JSX.Element => {
  const [totalEmp, setTotalEmp] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [pageCount, setPageCount] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [employerData, setEmployerData] = useState<OganizationAttributesType[]>(
    [],
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedData, setSelectedData] = useState<
    GridValidRowModel | undefined
  >();
  const pageInfo = useSelector((state) => state?.pagination?.employers);
  const formatNumber = (num: number): string => num.toLocaleString('en-IE');

  const updateEmployerData = async (): void => {
    await getAllOganizationData({
      page: Number(pageInfo?.page) + 1,
      size: pageInfo?.pageSize,
      sortBy: pageInfo?.sortBy,
      sortOrder: pageInfo?.sortOrder,
      searchValue,
    })
      .then((res) => {
        if (res && typeof res !== 'undefined') {
          setEmployerData(res.rows);
          setPageCount(res.count);
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  const getOrganizationDetails = async (organizationId: number): void => {
    await getOrganizationDetailsOrgId(organizationId)
      .then((res) => {
        const updatedAdminDetails = res?.employee.map(
          (employee: { employmentCategoryInfo: object }) => {
            const { employmentCategoryInfo, gender, maritalStatus, pepType } =
              employee.organizationEmployee;
            if (employmentCategoryInfo) {
              return {
                ...employee,
                gender,
                maritalStatus,
                pepType,
                economicStatus:
                  employmentCategoryInfo.parentId ?? employmentCategoryInfo.id,
                economicOccupation:
                  employmentCategoryInfo?.parentId !== null
                    ? employmentCategoryInfo?.id
                    : 0,
              };
            } else {
              return { ...employee, gender, maritalStatus, pepType };
            }
          },
        );
        setSelectedData({ ...res, employee: updatedAdminDetails });
        if (res) {
          setIsEditModalOpen(true);
        }
      })
      .catch((err: Error) => {
        console.log(err);
      });
  };

  useEffect(() => {
    updateEmployerData();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);

  const fetchInitialData = async (): Promise<void> => {
    try {
      const total: { totalEmployee: number } = await getTotalEmp();
      if (total?.totalEmployee) {
        setTotalEmp(total.totalEmployee || 0);
      }
    } catch (error) {
      console.error('Error fetching getTotalEmp:', error);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchData = async () => {
      await fetchInitialData();
    };

    void fetchData();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatAddress = (row: any, maxLength: number = 150): string => {
    const addressParts = [
      row.regStreetOne,
      row.regStreetTwo,
      row.regStreetThree?.trim() ? row.regStreetThree : null,
      row.regCity?.trim() ? row.regCity : null,
      row.regState?.trim() ? row.regState : null,
      row.regCountry?.trim() ? row.regCountry : null,
      row.regPostalCode?.trim() ? row.regPostalCode : null,
    ];

    const formattedAddress = addressParts.filter(Boolean).join(', ');

    return formattedAddress.length > maxLength
      ? `${formattedAddress.substring(0, maxLength)}...`
      : formattedAddress;
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'organizationId',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.organizationId}
        </Typography>
      ),
    },
    {
      field: 'uniqueId',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employersID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.uniqueId}
        </Typography>
      ),
    },
    {
      field: 'companyName',
      width: 190,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.companyName}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const maxLength = 20;
        const companyName: string = row.companyName;
        const truncatedName =
          companyName.length > maxLength
            ? companyName.slice(0, maxLength) + '...'
            : companyName;

        return (
          <Tooltip title={row.companyName} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {truncatedName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'payrollFrequency',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.payroll}
        </Typography>
      ),
      width: 150,
      renderCell: ({ row }) => {
        const payroll = String(row.payrollFrequency);
        const formattedPayroll =
          payroll.charAt(0).toUpperCase() + payroll.slice(1);

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {formattedPayroll}
          </Typography>
        );
      },
    },
    {
      field: 'payrollDate',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.payrollDateLabel}
        </Typography>
      ),
      width: 150,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.payrollDate ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'regEmail',
      width: 220,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.registerEmail}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const maxLength = 23;
        const email: string = row.regEmail;
        const truncatedEmail =
          email.length > maxLength ? email.slice(0, maxLength) + '...' : email;

        return (
          <Tooltip title={email} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {truncatedEmail}
            </Typography>
          </Tooltip>
        );
      },
    },

    {
      field: 'regPhoneNumber',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.registerPhone}
        </Typography>
      ),
      width: 150,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.regPhoneNumber}
        </Typography>
      ),
    },
    {
      field: 'regAddress',
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.registerAddress}
        </Typography>
      ),
      width: 170,
      renderCell: ({ row }) => (
        <Tooltip placement="top" title={formatAddress(row)}>
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {formatAddress(row, 20)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employerTaxDetails',
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employerTaxDetailsLabel}
        </Typography>
      ),
      width: 170,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.employerTaxDetails || '-'}
        </Typography>
      ),
    },
    {
      field: 'numberOfAdmin',
      sortable: true,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.noOfAdmins}
        </Typography>
      ),
      width: 120,
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          m="0 auto"
        >
          {row?.orgEmployeeCount || 0}
        </Typography>
      ),
    },
    {
      field: 'createdByAcorn',
      sortable: true,
      renderHeader: () => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'right' }}
        >
          {strings.displayText.createdByAcornText}
        </Typography>
      ),
      width: 130,
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {row?.createdByAcorn ? 'Yes' : 'No'}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.active}
        </Typography>
      ),
      width: 100,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <Box
            onClick={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleChange(e, row);
            }}
          >
            <Switch
              checked={row.isActive || false}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </Box>
        </Typography>
      ),
    },
  ];

  const handleCreateDialogClose = (): void => {
    setConfirmationModal(true);
  };

  const handleConfirmationModal = (): void => {
    setConfirmationModal(false);
    setIsModalOpen(false);
  };

  const handleConfirmationModalContinue = (): void => {
    setConfirmationModal(false);
  };

  const handleEditDialogClose = (): void => {
    setIsEditModalOpen(false);
  };

  const handleDeleteDialogClose = (): void => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedData?.organizationId !== undefined) {
      try {
        const res = await removeOganization(
          Number(selectedData.organizationId),
        );
        if (res) {
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.succesfullyDelete,
            }),
          );
          res && updateEmployerData();
        }
      } catch (err) {
        dispatch(
          setSnackBarFailed({ snackBarMessage: err?.response?.data?.msg }),
        );
      }
    }
    updateEmployerData();
    setOpenDeleteDialog(false);
  };

  const handleEvent: GridEventListener<'rowClick'> = (
    params: GridRowParams, // GridRowParams
    event: MuiEvent<React.MouseEvent<HTMLElement>>, // MuiEvent<React.MouseEvent<HTMLElement>>
  ) => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    navigate(`/employer-details/${params?.row?.organizationId}`);
    event.preventDefault();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: OganizationAttributesType,
  ): void => {
    if (row?.organizationId) {
      void updateOrganizationIsActive(row?.organizationId, {
        isActive: !row.isActive,
      })
        .then((res) => {
          if (typeof res !== 'undefined') {
            updateEmployerData();
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: `${row?.companyName ? row?.companyName : ''} ${
                  strings.displayText.EmployerStatusChange
                } ${!row.isActive ? 'active' : 'in-active'}`,
              }),
            );
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
    updateEmployerData();
    event.preventDefault();
    event.stopPropagation();
  };

  const downloadExcelFile = async (): Promise<void> => {
    try {
      const url = await fetchExportedEmployeeData();
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.ALL_EMPLOYEES;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.allEmployeesExportSuccess,
        }),
      );
    } catch (error) {
      console.log('Error', error);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.allEmployeesExportFailed,
        }),
      );
    }
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'employers', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'employers',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'employers', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'employers', value: 0 }));
  };

  return (
    <Fragment>
      <Box sx={{ cursor: 'pointer' }}>
        <CustomDataGrid
          title="Employers"
          subtitle={`[${strings.displayText.totalEmployees} : ${formatNumber(
            totalEmp,
          )}]`}
          columns={columns}
          searchEnabled={true}
          handleSearch={handleSearch}
          rows={employerData as GridRowData[]}
          onRowClick={handleEvent}
          addButtonTitle={addEmployerConstantTexts.addEmployer}
          actionBtnText2="delete"
          ActionBtnIcon2={() => <DeleteIcon />}
          actionBtnText1="edit"
          ActionBtnIcon1={() => <EditIcon />}
          isShowExport={true}
          onExportClick={() => {
            void downloadExcelFile();
          }}
          onAddButtonClick={() => {
            setIsModalOpen(true);
          }}
          onActionBtnClick1={(row, event) => {
            event.stopPropagation();
            getOrganizationDetails(row?.organizationId);
          }}
          onActionBtnClick2={(row, event) => {
            event.stopPropagation();
            if (row?.employee && row?.employee?.length > 0) {
              dispatch(
                setSnackBarFailed({
                  snackBarMessage:
                    strings.displayText.organizationDeleteRestrictText,
                }),
              );
            } else {
              setOpenDeleteDialog(true);
            }
            setSelectedData(row);
          }}
          getRowId={(row: OganizationAttributesType) =>
            `${String(row.organizationId)}${row.regEmail ? row.regEmail : ''}`
          }
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          pageCount={pageCount}
          currentPage="employers"
          sortModel={checkSortingValues(pageInfo)}
          sortingMode={strings.displayText.sortingServer}
          onSortModelChange={(mode) => {
            handleSortChange(mode);
          }}
        />
      </Box>

      <DialogBox
        open={isModalOpen}
        onClose={handleCreateDialogClose}
        title="Create Employer"
      >
        <AddEmployer
          onClose={setIsModalOpen}
          getupdateEmployerData={updateEmployerData}
        />
      </DialogBox>

      <ConfirmationModal
        open={confirmationModal}
        onClose={handleConfirmationModal}
        onContinue={handleConfirmationModalContinue}
      />

      <DialogBox
        open={isEditModalOpen}
        onClose={handleEditDialogClose}
        title="Edit Employer"
      >
        <EditEmployer
          data={selectedData as GridValidRowModel}
          onClose={setIsEditModalOpen}
          getupdateEmployerData={updateEmployerData}
        />
      </DialogBox>

      <AlertModal
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        handleDelete={handleDelete}
        title={`${strings.displayText.deleteConfirmationText} employer`}
        email={String(selectedData?.regEmail || selectedData?.companyName)}
      />
    </Fragment>
  );
};

export default Employers;
